import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { Badge, classNames, Icon, List } from '@pella/sash-framework';
import { CTA, Image } from '../../../';
import { isValidArray } from '../../../utilities/validations';

const HoverBanner = ({ template, className, compact }) => {
	const { hover_cards } = useStaticQuery(query).contentstackHoverConfig;
	const hover = hover_cards.find((hover) => hover.placement.includes(template));

	if (!hover) return null;

	const classes = classNames({
		'hover-banner grid': true,
		'bg-gray-5 grid-cols-1': ['product_finder', 'category_inspiration', 'shop_the_look'].includes(template),
		'border border-gray-10 md:pt-6 px-8 pt-8 xl:px-10 xl:pb-6 xl:grid-cols-[auto_1fr] xl:gap-x-10 mt-8 md:mt-6 max-xl:text-center': template === 'product_finder',
		'pt-10 px-5 text-center': template === 'category_inspiration',
		'border border-gray-10 px-5 pt-10 md:p-4 xl:p-6 md:grid-cols-[auto_1fr] md:max-xl:grid-rows-[1fr_auto] xl:gap-x-8 md:gap-x-6 items-center max-md:text-center': template === 'shop_the_look',
		'px-6 pb-8 pt-6 md:pb-8 md:px-14 border border-gray-42 max-w-[762px] md:grid-cols-2 md:gap-x-8 items-center': template === 'rta',
		'bg-white border border-black p-6 md:px-8 xl:px-10 grid-cols-1 md:grid-cols-[1fr_auto] sm:gap-x-8 xl:gap-x-10 items-center': template === 'confirmation',
		'xl:px-6 xl:pt-6 xl:pb-0 xl:grid-cols-1 max-w-[380px] mt-4 !items-start mb-10': template === 'rta' && compact,
		'xl:py-10 xl:gap-x-10 mb-10': template === 'rta' && !compact,
		[className]: className,
	});

	const imageClasses = classNames({
		'hover-banner-image mx-auto max-w-full': true,
		'rounded-t-[0.625rem]': ['product_finder', 'category_inspiration', 'shop_the_look'].includes(template),
		'col-start-1 row-start-3 xl:row-start-1 xl:row-span-4 xl:rounded-b-[0.625rem] aspect-[1.4] xl:aspect-[1.24] mt-8 md:mt-6 xl:mt-0 w-[clamp(207px,216px,216px)]': template === 'product_finder',
		'row-start-3 w-[clamp(120px,200px,220px)]': template === 'category_inspiration',
		'col-start-1 row-start-3 md:row-start-1 xl:row-span-4 md:rounded-b-[0.625rem] w-[170px] md:w-[104px] xl:w-[144px] md:aspect-square': template === 'shop_the_look',
		'col-start-1 md:col-start-2 row-start-1 md:row-span-2 rounded-[0.625rem] w-[188px] aspect-square md:w-[252px] md:h-[188px]': template === 'rta',
		'col-start-1 row-start-4 sm:row-start-1 sm:row-span-5 sm:col-start-2 w-[182px] md:w-[206px] xl:w-[246px] aspect-[1.36] md:aspect-[1.6] rounded-[0.625rem] mt-6 md:mt-0': template === 'confirmation',
		'xl:row-span-1 xl:col-start-1 xl:row-start-3 xl:w-[146px] xl:h-auto xl:aspect-[1.34] xl:rounded-b-none xl:mt-3 self-end': template === 'rta' && compact,
		'xl:w-[302px]': template === 'rta' && !compact,
	});

	const badgeClasses = classNames({
		'hover-banner-badge': true,
		'col-start-1 row-start-1 sm:row-start-2 mb-2': template === 'confirmation',
	});

	const headingClasses = classNames({
		'hover-banner-heading': true,
		'col-start-1 row-start-1 xl:col-start-2 xl:row-start-2 md:text-subtitle xl:font-normal max-w-[18ch] max-xl:mx-auto': template === 'product_finder',
		'row-start-1 mb-0 md:text-subtitle md:max-xl:font-normal xl:text-h3 max-w-[18ch] mx-auto text-pretty': template === 'category_inspiration',
		'row-start-1 xl:row-start-2 col-start-1 md:col-start-2 xl:text-subtitle mb-0 max-md:max-w-[18ch] max-md:mx-auto': template === 'shop_the_look',
		'col-start-1 row-start-2 md:row-start-1 mt-6 md:mt-0 text-center md:text-left max-md:max-w-[18ch] max-md:mx-auto mb-5 md:mb-6 xl:mb-4 xl:!text-subtitle xl:!leading-6 xl:!w-[18ch]': template === 'rta',
		'text-subtitle font-bold col-start-1 row-start-2 sm:row-start-3': template === 'confirmation',
	});

	const subheadingClasses = classNames({
		'hover-banner-subheading': true,
		'text-body-2 text-pretty text-gray-60 col-start-1 row-start-3 sm:row-start-4': template === 'confirmation',
	});

	const listClasses = classNames({
		'hover-banner-badge': true,
		'col-start-1 row-start-3 md:row-start-2 space-y-3 md:space-y-4': template === 'rta',
	});

	const ctaClasses = classNames({
		'hover-banner-badge': true,
		'col-start-1 row-start-2 xl:col-start-2 xl:row-start-3 mt-4': template === 'product_finder',
		'row-start-2 mb-6 mt-4 md:my-3 xl:my-6': template === 'category_inspiration',
		'row-start-2 md:row-start-2 xl:row-start-3 col-start-1 md:col-span-2 xl:col-start-2 xl:col-span-1 mt-4 mb-6 md:mb-0': template === 'shop_the_look',
	});

	const bulletHeadingClasses = classNames({
		'text-gray-60 xl:text-base/tight': true,
		'!text-body-2': compact,
	});

	return (
		<div
			className={classes}
			data-testid='hover-banner'
		>
			{isValidArray(hover.image) && (
				<Image
					data={hover.image[0]}
					className={imageClasses}
				/>
			)}

			{hover.badge && (
				<div className={badgeClasses}>
					<Badge
						color='primary'
						variant='rounded'
						size='sm'
						dangerouslySetInnerHTML={{ __html: hover.badge }}
						className='gap-x-0.5 [&>span:first-child]:!text-base'
					/>
				</div>
			)}

			{hover.heading && <h3 className={headingClasses}>{hover.heading}</h3>}

			{hover.subheading && <p className={subheadingClasses}>{hover.subheading}</p>}

			{isValidArray(hover.bullets) && (
				<List
					bullets='none'
					className={listClasses}
				>
					{hover.bullets.map((bullet, index) => (
						<li
							key={index}
							className='flex items-center gap-x-4 xl:!mt-2.5 xl:first:!mt-0'
						>
							{bullet.material_symbol && <Icon name={bullet.material_symbol} />}
							{bullet.file?.url && <Image data={{ image: bullet.file }} />}
							<span className={bulletHeadingClasses}>{bullet.heading}</span>
						</li>
					))}
				</List>
			)}

			{isValidArray(hover.cta) && (
				<div className={ctaClasses}>
					<CTA
						data={hover.cta[0]}
						size='sm'
						className='!w-fit'
					/>
				</div>
			)}
		</div>
	);
};

HoverBanner.propTypes = {
	template: PropTypes.string.isRequired,
	className: PropTypes.string,
	compact: PropTypes.bool,
};

export default HoverBanner;

export const query = graphql`
	{
		contentstackHoverConfig {
			hover_cards {
				placement
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90)
					}
				}
				badge
				heading
				subheading
				cta {
					...CTA
				}
				bullets {
					material_symbol
					file {
						url
						gatsbyImageData(quality: 90)
					}
					heading
				}
			}
		}
	}
`;
